import { ReactNode, useState } from "react";
import { InputText } from "@sellar-io/components";
import { useNavigate } from "react-router";
import * as ROUTES from "../../constants/routes";
import { InputLabel, InputContainer } from "@sellar-io/components";
import { useParamAsNumber } from "../../hooks/useParamAsNumber";
import { PRICE_LIST } from "../../constants/routes";
import { twMerge } from "tailwind-merge";
import { usePriceTier } from "../../hooks/api/v2/priceTier/usePriceTier";
import { usePriceTierUpdate } from "../../hooks/api/v2/priceTier/usePriceTierUpdate";
import { usePriceTierCreate } from "../../hooks/api/v2/priceTier/usePriceTierCreate";
import { formatCurrency, getDiscountDescription } from "@sellar-io/common";
import { Button, Icon } from "@sellar-io/components";
import { ListRuleUpdateDialog } from "./components/ListRuleUpdateDialog";
import { PriceTierDiscountDialogCreate } from "../priceTiers/priceTier/edit/PriceTierDiscountDialog/PriceTierDiscountDialogCreate";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import {
  PriceTierDiscountType,
  PriceTierGetResponseType,
} from "@sellar-io/api-v2/types";
import { PriceTierDiscountDialogUpdate } from "../priceTiers/priceTier/edit/PriceTierDiscountDialog/PriceTierDiscountDialogUpdate";
import { ContainerDiscountCreateDialog } from "./components/ContainerDiscountCreateDialog";
import { ContainerDiscountUpdateDialog } from "./components/ContainerDiscountUpdateDialog";
import { getContainerTypeLabel } from "./components/containerDiscountOptions";
import { usePriceListContext } from "./PriceListContext";
import { VolumeDiscountGroupAssignDialog } from "./components/VolumeDiscountGroupAssignDialog";
import { VolumeDiscountGroupListElement } from "./components/VolumeDiscountGroupListElement";
import { usePriceTierVolumeDiscountsList } from "../../hooks/api/v2/priceTierVolumeDiscounts/usePriceTierVolumeDiscountsList";
import { useRemoteFeatureFlags } from "../../hooks/useRemoteFeatureFlags";
import { AlertCompact } from "~/components/alert/AlertCompact";
import { DefaultPriceListTooltip } from "./components/DefaultPriceListTooltip";
import { useVolumeDiscountMode } from "../../hooks/useVolumeDiscountMode";
import { VolumeDiscountAdvancedSetupDialog } from "./components/VolumeDiscountModeDialogs";

export const PriceListDetail = () => {
  const priceListId = useParamAsNumber(PRICE_LIST, "priceListId");
  const navigate = useNavigate();

  const isNewTier = priceListId === undefined;

  const [editedName, setEditedName] = useState<string | null>(
    isNewTier ? "" : null,
  );

  const [updateListDiscountActive, setUpdateListDiscountActive] =
    useState(false);

  const [addContainerDiscountActive, setAddContainerDiscountActive] =
    useState(false);
  const [addVolumeDiscountActive, setAddVolumeDiscountActive] = useState(false);
  const [addVolumeDiscountGroupActive, setAddVolumeDiscountGroupActive] =
    useState(false);
  const [advancedSetupDialogActive, setAdvancedSetupDialogActive] =
    useState(false);

  const volumeDiscountMode = useVolumeDiscountMode();
  const unbundlingEnabled = useRemoteFeatureFlags().isActive(
    "unbundlingReleaseFinal",
  );

  const { data: priceList, isFetched } = usePriceTier(priceListId);

  const { data: volumeDiscountAssignments } = usePriceTierVolumeDiscountsList(
    priceListId,
    {
      enabled: unbundlingEnabled && !!priceListId,
    },
  );

  const sortedVolumeDiscountAssignments = volumeDiscountAssignments?.sort(
    (a) => (a.fulfillmentType === null ? -1 : 1),
  );

  const isBaseList = priceList?.derivationType === "base";
  const isMigratedList = !isBaseList && !priceList?.BasePriceTier;

  const priceListUpdate = usePriceTierUpdate(priceListId);
  const priceListCreate = usePriceTierCreate();

  const name = editedName ?? priceList?.name ?? "";
  const showNameSave =
    editedName !== null &&
    (isNewTier || (editedName !== priceList?.name && editedName.length > 0));

  const priceListProcessing =
    priceListUpdate.isPending || priceListCreate.isPending;

  return (
    <>
      <div className="flex flex-col gap-4 p-4">
        {priceList?.defaultType && (
          <DefaultPriceListTooltip hasMultipleDefaults={false}>
            <AlertCompact className="bg-zinc-100">
              <span>
                This is your <b>{priceList.defaultType} price list</b>
              </span>
            </AlertCompact>
          </DefaultPriceListTooltip>
        )}
        <form
          className="mx-auto flex w-full max-w-230 flex-col gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (isNewTier) {
              priceListCreate.mutate(
                {
                  name,
                  minimumOrderPrice: null,
                },
                {
                  onSuccess: (response) => {
                    setEditedName(null);
                    navigate(
                      ROUTES.PRICE_LIST.href({ priceListId: response.id }),
                    );
                  },
                },
              );
            } else {
              if (name)
                priceListUpdate.mutate(
                  {
                    name,
                    minimumOrderPrice: null,
                    derivationType: priceList?.derivationType,
                  },
                  {
                    onSuccess: () => {
                      setEditedName(null);
                    },
                  },
                );
            }
          }}
        >
          <InputLabel>Name</InputLabel>
          <div className="flex">
            <InputContainer
              className={twMerge(
                "relative flex w-full items-center gap-1",
                showNameSave && "rounded-r-none",
              )}
            >
              <InputText
                value={name}
                onChange={(e) => setEditedName(e.target.value)}
              />
            </InputContainer>
            {showNameSave && (
              <Button
                type="submit"
                size="small"
                className="rounded-l-none px-4"
                loading={priceListProcessing}
                disabled={priceListProcessing || !name}
              >
                {isNewTier ? "Create" : "Update"}
              </Button>
            )}
          </div>
          {priceList?.BasePriceTier && (
            <p className="text-zinc-500">
              Derived from '{priceList.BasePriceTier.name}'
            </p>
          )}
        </form>

        {!isBaseList && isFetched && priceList && (
          <>
            <div className="flex flex-col gap-2">
              <InputLabel>Top-level discounts</InputLabel>
              {["flat", "percentage"].includes(priceList.derivationType) && (
                <DiscountElement
                  onClick={() => setUpdateListDiscountActive(true)}
                >
                  {priceList.derivationType === "percentage" && (
                    <>{priceList.derivationValue}% off all products</>
                  )}
                  {priceList.derivationType === "flat" && (
                    <>
                      {formatCurrency(priceList.derivationValue ?? 0)} off all
                      products
                    </>
                  )}
                  {!isMigratedList && (
                    <Button
                      size={"small"}
                      color={"transparent"}
                      className="p-1"
                    >
                      <Icon icon={"PENCIL"} className="text-black" size={0.7} />
                    </Button>
                  )}
                </DiscountElement>
              )}
              {priceList.PriceTierContainerDiscounts.map((discount) => (
                <ContainerDiscountListElement
                  key={discount.id}
                  discount={discount}
                  canEdit={!isMigratedList}
                />
              ))}
              {!isMigratedList && (
                <Button
                  className="py-3"
                  onClick={() => setAddContainerDiscountActive(true)}
                >
                  Add
                </Button>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <InputLabel>
                {unbundlingEnabled
                  ? "Volume discount sets"
                  : "Volume discounts"}
              </InputLabel>
              {unbundlingEnabled ? (
                <>
                  {sortedVolumeDiscountAssignments?.map((assignment) => (
                    <VolumeDiscountGroupListElement
                      key={assignment.id}
                      assignment={assignment}
                      priceTierId={priceListId}
                      isBasicMode={volumeDiscountMode.isBasicMode}
                      canEdit={!isMigratedList}
                    >
                      {assignment.VolumeDiscounts.PriceTierDiscounts.map(
                        (discount) => (
                          <VolumeDiscountListElement
                            key={discount.id}
                            discount={discount}
                            canEdit={!isMigratedList}
                          />
                        ),
                      )}
                    </VolumeDiscountGroupListElement>
                  ))}
                  {!isMigratedList && (
                    <Button
                      className="py-3"
                      onClick={() => setAddVolumeDiscountGroupActive(true)}
                      disabled={
                        volumeDiscountMode.isBasicMode &&
                        (sortedVolumeDiscountAssignments?.length ?? 0) > 0
                      }
                    >
                      Add
                    </Button>
                  )}
                </>
              ) : (
                <>
                  {priceList.VolumeDiscounts &&
                    priceList.VolumeDiscounts.PriceTierDiscounts.map(
                      (discount) => (
                        <VolumeDiscountListElement
                          key={discount.id}
                          discount={discount}
                          canEdit={!isMigratedList}
                        />
                      ),
                    )}
                  {!isMigratedList && (
                    <Button
                      className="py-3"
                      onClick={() => setAddVolumeDiscountActive(true)}
                    >
                      Add
                    </Button>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>
      {isFetched && priceList?.derivationType && !isMigratedList && (
        <ListRuleUpdateDialog
          active={updateListDiscountActive}
          setActive={setUpdateListDiscountActive}
          derivationType={priceList.derivationType}
          derivationValue={priceList.derivationValue}
          id={priceListId}
        />
      )}
      {priceList && (
        <ContainerDiscountCreateDialog
          priceListId={priceListId}
          active={addContainerDiscountActive}
          setActive={setAddContainerDiscountActive}
          activeContainers={[
            ...priceList.PriceTierContainerDiscounts.map(
              (discount) => discount.containerType,
            ),
            ...(priceList &&
            ["flat", "percentage"].includes(priceList.derivationType)
              ? ["any" as const]
              : []),
          ]}
        />
      )}
      <PriceTierDiscountDialogCreate
        priceTierId={priceListId}
        volumeDiscountsId={priceList?.VolumeDiscounts?.id ?? null}
        active={addVolumeDiscountActive}
        onDismiss={() => setAddVolumeDiscountActive(false)}
      />
      {unbundlingEnabled && priceListId && (
        <VolumeDiscountGroupAssignDialog
          active={addVolumeDiscountGroupActive}
          setActive={setAddVolumeDiscountGroupActive}
          priceListId={priceListId}
          existingAssignments={
            volumeDiscountAssignments?.map((a) => a.fulfillmentType) ?? []
          }
          onSetUpConditionalVolumeDiscounts={() =>
            setAdvancedSetupDialogActive(true)
          }
        />
      )}
      <VolumeDiscountAdvancedSetupDialog
        active={advancedSetupDialogActive}
        onDismiss={() => setAdvancedSetupDialogActive(false)}
        onEnabled={() => setAddVolumeDiscountGroupActive(true)}
      />
    </>
  );
};

const ContainerDiscountListElement = ({
  discount,
  canEdit = true,
}: {
  discount: PriceTierGetResponseType["PriceTierContainerDiscounts"][number];
  canEdit: boolean;
}) => {
  const [editDialogActive, setEditDialogActive] = useState(false);

  const discountValue =
    discount.derivationType === "flat"
      ? formatCurrency(discount.derivationValue)
      : `${discount.derivationValue}%`;
  const containerLabel = getContainerTypeLabel(discount.containerType);

  return (
    <>
      <DiscountElement onClick={() => setEditDialogActive(true)}>
        {discountValue} off {containerLabel}
        {canEdit && (
          <Button size={"small"} color={"transparent"} className="p-1">
            <Icon icon={"PENCIL"} className="text-black" size={0.7} />
          </Button>
        )}
      </DiscountElement>
      {canEdit && (
        <ContainerDiscountUpdateDialog
          active={editDialogActive}
          setActive={setEditDialogActive}
          discount={discount}
        />
      )}
    </>
  );
};

const VolumeDiscountListElement = ({
  discount,
  canEdit = true,
}: {
  discount: Omit<
    PriceTierDiscountType,
    "createdAt" | "updatedAt" | "deletedAt" | "VolumeDiscountsId"
  >;
  canEdit: boolean;
}) => {
  const {
    discountsPreviewEnabled,
    setDiscountsPreviewEnabled,
    setDiscountsPreviewType,
    setSelectedDiscount,
    selectedDiscount,
    setMinimumSpend,
    setNumberOfUnits,
  } = usePriceListContext();

  const { business } = useSellarCurrentBusiness();
  const [editDialogActive, setEditDialogActive] = useState(false);

  const currency = business?.Supplier?.currency;
  const discountDescription = getDiscountDescription({ discount, currency });

  const previewEnabled =
    discountsPreviewEnabled && discount.id === selectedDiscount?.id;

  const shouldEnable = !previewEnabled;

  const unbundlingEnabled = useRemoteFeatureFlags().isActive(
    "unbundlingReleaseFinal",
  );

  return (
    <>
      <DiscountElement onClick={() => setEditDialogActive(true)}>
        {discountDescription}
        {canEdit && (
          <div className="flex gap-2">
            <Button
              size={"small"}
              color={"transparent"}
              className={twMerge("p-1", previewEnabled && "bg-zinc-200")}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDiscountsPreviewEnabled?.(shouldEnable);
                setDiscountsPreviewType?.(
                  discount.minimumBasketValue ? "spend" : "unit",
                );
                setMinimumSpend?.(
                  shouldEnable && discount.minimumBasketValue
                    ? discount.minimumBasketValue
                    : null,
                );
                setNumberOfUnits?.(
                  shouldEnable && discount.quantity ? discount.quantity : null,
                );
                setSelectedDiscount?.(shouldEnable ? discount : undefined);
              }}
            >
              <Icon icon={"EYE"} className="text-black" size={0.7} />
            </Button>
            {!unbundlingEnabled && (
              <Button size={"small"} color={"transparent"} className="p-1">
                <Icon icon={"PENCIL"} className="text-black" size={0.7} />
              </Button>
            )}
          </div>
        )}
      </DiscountElement>
      {canEdit && !unbundlingEnabled && (
        <PriceTierDiscountDialogUpdate
          priceTierDiscount={discount}
          active={editDialogActive}
          onDismiss={() => setEditDialogActive(false)}
        />
      )}
    </>
  );
};

const DiscountElement = ({
  children,
  onClick,
}: {
  children: ReactNode;
  onClick?: () => void;
}) => {
  return (
    <div
      className="flex cursor-pointer items-center justify-between gap-3 overflow-hidden rounded-lg border border-solid bg-white p-3 hover:border-zinc-700"
      onClick={onClick}
    >
      {children}
    </div>
  );
};
